import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import {
  EmptyState,
  MeterRow,
  Modal,
  PageHeader,
  Panel,
  RefreshButton,
  StatCard,
} from '../components/ui';
import {
  IconActivity,
  IconAIVoice,
  IconAnswerRate,
  IconArrow,
  IconBrandWhatsApp,
  IconCallAnswered,
  IconCallMissed,
  IconCallPlaced,
  IconDiagnosis,
  IconError,
  IconExternal,
  IconPatients,
  IconPatientNew,
  IconPending,
  IconSmartSMS,
  IconSMS,
  IconSuccess,
  IconVoiceCall,
  IconVoiceSMS,
} from '../components/icons';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) => {
  if (n === null || n === undefined) return '—';
  const num = Number(n);
  if (isNaN(num)) return '—';
  return num.toLocaleString('en-US');
};

const relativeTime = (dateStr) => {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
};

const maskPhone = (phone) => {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 6) return phone;
  return digits.slice(0, 3) + '****' + digits.slice(-3);
};

const VOICE_CHANNELS = ['ivr', 'voice', 'elevenlabs_ivr'];


const campaignTypeLabel = (type) => {
  const map = {
    sms: 'SMS',
    voice: 'Voice SMS',
    whatsapp: 'WhatsApp',
    smart_sms: 'Smart SMS',
    elevenlabs_ivr: 'AI Voice',
  };
  return map[type] || type || 'Campaign';
};

// ─── Activity feed ────────────────────────────────────────────────────────────

/**
 * Tile colours, keyed by outcome rather than by channel.
 *
 * Colour here is information, not decoration: green means it reached the
 * patient, rose means it did not, amber means the line rang out unanswered.
 * Scanning the feed for failures is the actual job, and the tint does that
 * faster than reading every title.
 *
 * Written out as whole class names because Tailwind scans source text — a
 * built-up `bg-${tone}-50` would not survive the JIT pass.
 */
const TONES = {
  emerald: 'bg-emerald-50 text-emerald-600 ring-emerald-100',
  rose: 'bg-rose-50 text-rose-600 ring-rose-100',
  amber: 'bg-amber-50 text-amber-600 ring-amber-100',
  sky: 'bg-sky-50 text-sky-600 ring-sky-100',
  violet: 'bg-violet-50 text-violet-600 ring-violet-100',
  slate: 'bg-slate-50 text-slate-500 ring-slate-200',
};

/**
 * WhatsApp keeps its own brand green instead of an outcome tone, because the
 * mark is only recognisable in the colour people already know it by. The
 * failure case still goes rose — a red-tinted tile reads as "did not arrive"
 * more clearly than a green one with a red word next to it.
 */
const WHATSAPP_TILE = 'bg-[#25D366]/10 text-[#25D366] ring-[#25D366]/20';

const ACTIVITY_STYLES = {
  whatsapp_delivered: { icon: IconBrandWhatsApp, tile: WHATSAPP_TILE, brand: true },
  whatsapp_failed: { icon: IconBrandWhatsApp, tone: 'rose' },
  sms_delivered: { icon: IconSMS, tone: 'sky' },
  sms_failed: { icon: IconSMS, tone: 'rose' },
  sms_sent: { icon: IconSMS, tone: 'slate' },
  voice_answered: { icon: IconCallAnswered, tone: 'emerald' },
  voice_missed: { icon: IconCallMissed, tone: 'amber' },
  // A call that never connected is a fault, not a missed call — rose, like
  // the other failures, so it does not read as the patient's doing.
  voice_failed: { icon: IconCallMissed, tone: 'rose' },
  voice_placed: { icon: IconCallPlaced, tone: 'sky' },
  campaign_completed: { icon: IconSuccess, tone: 'emerald' },
  campaign_sms: { icon: IconSMS, tone: 'sky' },
  campaign_voice: { icon: IconVoiceSMS, tone: 'sky' },
  campaign_ivr: { icon: IconVoiceSMS, tone: 'sky' },
  campaign_elevenlabs_ivr: { icon: IconAIVoice, tone: 'violet' },
  campaign_smart_sms: { icon: IconSmartSMS, tone: 'violet' },
  campaign_whatsapp: { icon: IconBrandWhatsApp, tile: WHATSAPP_TILE, brand: true },
  patient: { icon: IconPatientNew, tone: 'violet' },
};

/**
 * Rows shown before the feed is expanded.
 *
 * Five, to sit level with the five diagnosis rows in the panel beside it. The
 * feed used to render every item it had — around thirty — so the left card ran
 * three times the height of the right one and the row below it was pushed off
 * the first screen.
 */
const ACTIVITY_PREVIEW = 5;

const ActivityFeed = ({ items }) => (
  <ol className="divide-y divide-gray-100">
    {items.map((item, idx) => {
      const style = ACTIVITY_STYLES[item.type] || ACTIVITY_STYLES.patient;
      const Icon = style.icon;
      const tile = style.tile || TONES[style.tone] || TONES.slate;
      return (
        <li key={idx} className="flex items-center gap-3 py-2.5">
          <span
            className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl ring-1 ring-inset ${tile}`}
          >
            {/* brand={false} makes the mark inherit the tile's ink, so the
                failure tile turns the logo rose instead of leaving it green. */}
            <Icon size={18} brand={style.brand ? undefined : false} aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-medium text-gray-900">{item.title}</p>
            {item.subtitle && (
              <p className="mt-0.5 truncate text-xs text-gray-400">{item.subtitle}</p>
            )}
          </div>
          <span className="flex-shrink-0 whitespace-nowrap text-xs tabular-nums text-gray-400">
            {item.time}
          </span>
        </li>
      );
    })}
  </ol>
);

const ActivitySkeleton = () => (
  <div className="space-y-4 py-1">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="flex items-center gap-3">
        <div className="h-[30px] w-[30px] flex-shrink-0 animate-pulse rounded-full bg-gray-100" />
        <div className="flex-1 space-y-2">
          <div className="h-3 w-3/5 animate-pulse rounded bg-gray-100" />
          <div className="h-2.5 w-2/5 animate-pulse rounded bg-gray-100" />
        </div>
      </div>
    ))}
  </div>
);

// ─── Quick actions ────────────────────────────────────────────────────────────

const QUICK_ACTIONS = [
  { to: '/smart-sms', icon: IconSmartSMS, label: 'Smart SMS', hint: 'Personalised messages' },
  { to: '/voice-sms', icon: IconVoiceSMS, label: 'Voice SMS', hint: 'Recorded broadcast' },
  { to: '/voice-calls', icon: IconAIVoice, label: 'AI Voice Calls', hint: 'Conversational agent' },
  { to: '/patients', icon: IconPatients, label: 'Manage Patients', hint: 'Roster & diagnoses' },
];

// ─── Dashboard ────────────────────────────────────────────────────────────────

const Dashboard = () => {
  const { user } = useAuth();

  // Main stat counts
  const [patientCount, setPatientCount] = useState(null);
  const [smsSentCount, setSmsSentCount] = useState(null);
  const [voiceCallCount, setVoiceCallCount] = useState(null);
  const [answerRate, setAnswerRate] = useState(null);

  // Week-over-week trend deltas
  const [patientTrend, setPatientTrend] = useState(null);
  const [smsTrend, setSmsTrend] = useState(null);
  const [voiceTrend, setVoiceTrend] = useState(null);
  const [answerRateTrend, setAnswerRateTrend] = useState(null);

  const [recentActivity, setRecentActivity] = useState([]);
  const [activityOpen, setActivityOpen] = useState(false);
  const [diagnosisBreakdown, setDiagnosisBreakdown] = useState([]);
  const [pendingReviews, setPendingReviews] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchDashboardData = useCallback(async (showRefreshing = false) => {
    // Without a logged-in user we cannot scope queries by user_id —
    // and querying without that scope would either return another
    // tenant's rows (under permissive RLS) or 0 (under strict RLS).
    // Bail rather than render misleading numbers.
    if (!user?.id) {
      setLoading(false);
      setRefreshing(false);
      return;
    }
    const userId = user.id;
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    setError(null);

    const now = new Date();
    const fourteenDaysAgoISO = new Date(now.getTime() - 14 * 86400000).toISOString();
    // 90-day window for "all-time" totals so the unbounded scan is bounded.
    // Adjust if your retention model expects longer.
    const ninetyDaysAgoISO = new Date(now.getTime() - 90 * 86400000).toISOString();

    try {
      const [
        patientsRes,           // 0: all patients
        campaignsRes,          // 1: recent campaigns
        interactionsRes,       // 2: 90-day interactions
        reviewsRes,            // 3: pending reviews count
        weeklyInteractionsRes, // 4: 14-day interactions (trends + sparklines)
        recentInteractionsRes, // 5: recent interactions (activity feed)
      ] = await Promise.allSettled([
        // 0
        api.get('/patients', { params: { user_id: userId } }),
        // 1
        api.get('/campaigns', { params: { user_id: userId, limit: 10 } }),
        // 2
        api.get('/analytics/interactions', {
          params: { user_id: userId, since: ninetyDaysAgoISO, limit: 10000 },
        }),
        // 3
        api.get('/reviews/list', { params: { user_id: userId, status: 'pending' } }),
        // 4
        api.get('/analytics/interactions', {
          params: { user_id: userId, since: fourteenDaysAgoISO, limit: 10000 },
        }),
        // 5
        api.get('/analytics/interactions', {
          params: { user_id: userId, limit: 10, sort: 'desc' },
        }),
      ]);

      // ── Total patients ──────────────────────────────────────────────────────
      const allPatients = patientsRes.status === 'fulfilled'
        ? (patientsRes.value.data?.patients ?? []) : [];
      setPatientCount(allPatients.length);

      // New patients this week
      const newThisWeek = allPatients.filter(
        (p) => p.created_at && new Date(p.created_at).getTime() >= now.getTime() - 7 * 86400000
      );
      if (newThisWeek.length > 0) setPatientTrend(newThisWeek.length);

      // Diagnosis breakdown
      const diagCounts = {};
      allPatients.forEach((p) => {
        if (!p.diagnosis) return;
        diagCounts[p.diagnosis] = (diagCounts[p.diagnosis] || 0) + 1;
      });
      if (Object.keys(diagCounts).length > 0) {
        setDiagnosisBreakdown(
          Object.entries(diagCounts)
            .map(([diagnosis, count]) => ({ diagnosis, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 5)
        );
      }

      // ── SMS + Voice totals from ALL interactions ────────────────────────────
      if (interactionsRes.status === 'fulfilled') {
        const interactions = interactionsRes.value.data?.interactions ?? [];
        const smsRows = interactions.filter((i) => i.channel === 'sms');
        const voiceRows = interactions.filter((i) =>
          VOICE_CHANNELS.includes(i.channel)
        );
        const answered = voiceRows.filter((i) => i.call_answered);
        setSmsSentCount(smsRows.length);
        setVoiceCallCount(voiceRows.length);
        setAnswerRate(
          voiceRows.length > 0
            ? Math.round((answered.length / voiceRows.length) * 100)
            : null
        );
      }

      // ── Interaction trend badges + sparklines ───────────────────────────────
      if (weeklyInteractionsRes.status === 'fulfilled') {
        const items = weeklyInteractionsRes.value.data?.interactions ?? [];
        const cutoff = now.getTime() - 7 * 86400000;

        const thisWeek = items.filter((i) => new Date(i.created_at).getTime() >= cutoff);
        const lastWeek = items.filter((i) => new Date(i.created_at).getTime() < cutoff);

        const smsThis = thisWeek.filter((i) => i.channel === 'sms').length;
        const smsLast = lastWeek.filter((i) => i.channel === 'sms').length;
        if (smsThis > 0 || smsLast > 0) setSmsTrend(smsThis - smsLast);

        const voiceThis = thisWeek.filter((i) => VOICE_CHANNELS.includes(i.channel)).length;
        const voiceLast = lastWeek.filter((i) => VOICE_CHANNELS.includes(i.channel)).length;
        if (voiceThis > 0 || voiceLast > 0) setVoiceTrend(voiceThis - voiceLast);

        const vThis = thisWeek.filter((i) => VOICE_CHANNELS.includes(i.channel));
        const vLast = lastWeek.filter((i) => VOICE_CHANNELS.includes(i.channel));
        const arThis = vThis.length > 0
          ? Math.round((vThis.filter((i) => i.call_answered).length / vThis.length) * 100) : null;
        const arLast = vLast.length > 0
          ? Math.round((vLast.filter((i) => i.call_answered).length / vLast.length) * 100) : null;
        if (arThis !== null && arLast !== null) setAnswerRateTrend(arThis - arLast);
        else if (arThis !== null) setAnswerRateTrend(arThis);
        else if (arLast !== null) setAnswerRateTrend(-arLast);
      }

      // ── Pending reviews ─────────────────────────────────────────────────────
      if (reviewsRes.status === 'fulfilled') {
        const reviews = reviewsRes.value.data?.reviews ?? [];
        setPendingReviews(reviews.length);
      }

      // ── Build Recent Activity feed ──────────────────────────────────────────
      const activityItems = [];

      // Source 1: Recent campaign_interactions
      if (recentInteractionsRes.status === 'fulfilled') {
        const rows = recentInteractionsRes.value.data?.interactions ?? [];
        rows.forEach((row) => {
          // Each channel is matched by name. The previous version asked only
          // "is this SMS?" and sent everything else down the voice branch, so
          // every WhatsApp row — a third of the feed — was captioned "Voice
          // call — no answer", the call_answered column being null for a
          // channel that has no calls.
          const ch = row.channel || 'sms';
          const failed = row.status === 'failed';
          let type;
          let title;
          if (ch === 'whatsapp') {
            type = failed ? 'whatsapp_failed' : 'whatsapp_delivered';
            title = failed ? 'WhatsApp message failed' : 'WhatsApp message delivered';
          } else if (ch === 'voice' || ch === 'ivr' || ch === 'elevenlabs_ivr') {
            // "failed" and "no answer" are different things and were swapped.
            // A call that never reached the handset — a rejected SIP INVITE, a
            // provider error — was captioned "Voice call — no answer", which
            // reads as "we rang them and nobody picked up" and sends you
            // looking at the patient instead of at the trunk. Meanwhile a real
            // no_answer fell through to "Voice call placed".
            const answered = Boolean(row.call_answered) || row.status === 'answered';
            const noAnswer = row.status === 'no_answer' || row.status === 'unanswered';
            if (answered) {
              type = 'voice_answered';
              title = 'Voice call answered';
            } else if (failed) {
              type = 'voice_failed';
              title = 'Voice call failed — never connected';
            } else if (noAnswer) {
              type = 'voice_missed';
              title = 'Voice call — no answer';
            } else {
              type = 'voice_placed';
              title = 'Voice call placed';
            }
          } else {
            type = failed ? 'sms_failed' : row.status === 'delivered' ? 'sms_delivered' : 'sms_sent';
            title = failed ? 'SMS failed' : row.status === 'delivered' ? 'SMS delivered' : 'SMS sent';
          }
          activityItems.push({
            type,
            title,
            subtitle: row.phone_number ? maskPhone(row.phone_number) : '',
            time: relativeTime(row.created_at),
            _ts: row.created_at ? new Date(row.created_at).getTime() : 0,
          });
        });
      }

      // Source 2: Recent patients (from already-fetched allPatients, most recent 5)
      const recentPatients = allPatients
        .slice()
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 5);
      recentPatients.forEach((p) => {
        activityItems.push({
          type: 'patient',
          title: p.name ? `${p.name} enrolled` : 'New patient enrolled',
          subtitle: p.diagnosis
            ? `Diagnosis: ${p.diagnosis}`
            : p.phone
            ? maskPhone(p.phone)
            : '',
          time: relativeTime(p.created_at),
          _ts: p.created_at ? new Date(p.created_at).getTime() : 0,
        });
      });

      // Source 3: Recent campaigns
      if (campaignsRes.status === 'fulfilled') {
        const campaigns = campaignsRes.value.data?.campaigns ?? [];
        campaigns.slice(0, 10).forEach((c) => {
          const ts = c.completed_at || c.started_at || c.created_at;
          const typeKey =
            c.status === 'completed'
              ? 'campaign_completed'
              : `campaign_${c.type || 'sms'}`;
          const parts = [
            c.total_recipients ? `${fmt(c.total_recipients)} recipients` : null,
            c.type ? campaignTypeLabel(c.type) : null,
          ].filter(Boolean);
          activityItems.push({
            type: typeKey,
            title: c.name || 'Campaign',
            subtitle: parts.join(' · '),
            time: relativeTime(ts),
            _ts: ts ? new Date(ts).getTime() : 0,
          });
        });
      }

      // Sort by newest first, cap at 12
      activityItems.sort((a, b) => b._ts - a._ts);
      setRecentActivity(activityItems.slice(0, 12));
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setError('Failed to load some dashboard data. Please refresh.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // ── Stat card definitions ─────────────────────────────────────────────────
  const stats = [
    {
      label: 'Total Patients',
      value: fmt(patientCount),
      icon: IconPatients,
      delta: { value: patientTrend, suffix: '', positiveIsGood: true },
      footnote:
        patientCount === 0 ? 'Add patients to get started' : 'new this week',
    },
    {
      label: 'SMS Sent',
      value: fmt(smsSentCount),
      icon: IconSMS,
      delta: { value: smsTrend, suffix: '', positiveIsGood: true },
      footnote: smsSentCount === 0 ? 'No SMS campaigns yet' : 'vs. last week',
    },
    {
      label: 'Voice Calls',
      value: fmt(voiceCallCount),
      icon: IconVoiceCall,
      delta: { value: voiceTrend, suffix: '', positiveIsGood: true },
      footnote: voiceCallCount === 0 ? 'No voice campaigns yet' : 'vs. last week',
    },
    {
      label: 'Answer Rate',
      value:
        answerRate !== null
          ? `${answerRate}%`
          : voiceCallCount === 0
          ? '—'
          : '0%',
      icon: IconAnswerRate,
      delta: { value: answerRateTrend, suffix: '%', positiveIsGood: true },
      footnote:
        voiceCallCount === 0
          ? 'No voice campaigns yet'
          : answerRate !== null
          ? `${fmt(Math.round((voiceCallCount * answerRate) / 100))} of ${fmt(voiceCallCount)} answered`
          : 'Awaiting call data',
    },
  ];

  const diagnosisTotal = diagnosisBreakdown.reduce((s, d) => s + d.count, 0);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Overview"
        description="AI-powered patient health education platform"
      >
        {pendingReviews > 0 && (
          <Link
            to="/review-queue"
            className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-1.5 text-[13px] font-medium text-amber-800 transition-colors hover:bg-amber-50"
          >
            <IconPending size={15} aria-hidden="true" />
            {pendingReviews} pending review{pendingReviews !== 1 ? 's' : ''}
          </Link>
        )}
        <RefreshButton busy={refreshing} onClick={() => fetchDashboardData(true)} />
      </PageHeader>

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50/60 px-4 py-3 text-sm text-rose-700">
          <IconError size={16} className="flex-shrink-0" aria-hidden="true" />
          {error}
        </div>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <StatCard key={stat.label} {...stat} loading={loading} />
        ))}
      </div>

      {/* Activity + diagnoses */}
      {/* Both cards stretch to a shared height. The activity card never grows:
          the full list opens in a dialog, so nothing below it moves. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5 lg:items-stretch">
        <Panel
          className="lg:col-span-3"
          title="Recent Activity"
          description="Latest campaign events and patient enrolments"
          action={
            <Link
              to="/analytics"
              className="inline-flex items-center gap-1 text-[13px] font-medium text-gray-500 transition-colors hover:text-gray-900"
            >
              Full analytics
              <IconArrow size={14} aria-hidden="true" />
            </Link>
          }
        >
          {loading ? (
            <ActivitySkeleton />
          ) : recentActivity.length === 0 ? (
            <EmptyState
              icon={IconActivity}
              title="No recent activity yet"
              description="Campaign sends and patient enrolments will appear here as they happen."
              action={
                <Link to="/smart-sms" className="font-medium text-primary-600 hover:text-primary-700">
                  Start your first campaign →
                </Link>
              }
            />
          ) : (
            <>
              <ActivityFeed items={recentActivity.slice(0, ACTIVITY_PREVIEW)} />
              {recentActivity.length > ACTIVITY_PREVIEW && (
                <button
                  type="button"
                  onClick={() => setActivityOpen(true)}
                  className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 py-2 text-xs font-medium text-gray-500 transition-colors hover:border-gray-300 hover:text-gray-900"
                >
                  Show all {recentActivity.length}
                  <IconArrow size={14} aria-hidden="true" />
                </button>
              )}
            </>
          )}
        </Panel>

        <Panel
          className="lg:col-span-2"
          title="Patient Diagnoses"
          description={
            diagnosisTotal > 0 ? `Top ${diagnosisBreakdown.length} of ${fmt(diagnosisTotal)} recorded` : 'Distribution across your roster'
          }
          action={
            <Link
              to="/patients"
              className="inline-flex items-center gap-1 text-[13px] font-medium text-gray-500 transition-colors hover:text-gray-900"
            >
              View all
              <IconArrow size={14} aria-hidden="true" />
            </Link>
          }
        >
          {loading ? (
            <div className="space-y-5 py-1">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="space-y-2">
                  <div className="h-3 w-1/2 animate-pulse rounded bg-gray-100" />
                  <div className="h-1.5 w-full animate-pulse rounded-full bg-gray-100" />
                </div>
              ))}
            </div>
          ) : diagnosisBreakdown.length === 0 ? (
            <EmptyState
              icon={IconDiagnosis}
              title="No diagnoses recorded"
              description="Add a diagnosis to a patient record to see the distribution here."
              action={
                <Link to="/patients" className="font-medium text-primary-600 hover:text-primary-700">
                  Add patients →
                </Link>
              }
            />
          ) : (
            <div className="space-y-4">
              {diagnosisBreakdown.map(({ diagnosis, count }, i) => {
                const pct = diagnosisTotal > 0 ? Math.round((count / diagnosisTotal) * 100) : 0;
                return (
                  <MeterRow
                    key={diagnosis}
                    rank={i + 1}
                    label={diagnosis}
                    value={pct}
                    hint={`${count} patient${count !== 1 ? 's' : ''}`}
                    valueLabel={`${pct}%`}
                  />
                );
              })}
            </div>
          )}
        </Panel>
      </div>

      {/* Quick actions */}
      <Panel title="Quick Actions" description="Jump straight into a channel">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {QUICK_ACTIONS.map(({ to, icon: Icon, label, hint }) => (
            <Link
              key={to}
              to={to}
              className="group flex items-center gap-3 rounded-lg border border-gray-200 px-4 py-3 transition-colors hover:border-gray-300 hover:bg-gray-50/60"
            >
              <Icon
                size={19}
                className="flex-shrink-0 text-gray-400 transition-colors group-hover:text-gray-900"
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13px] font-medium text-gray-900">{label}</span>
                <span className="block truncate text-xs text-gray-400">{hint}</span>
              </span>
              <IconExternal
                size={14}
                className="flex-shrink-0 text-gray-300 transition-colors group-hover:text-gray-500"
                aria-hidden="true"
              />
            </Link>
          ))}
        </div>
      </Panel>

      {/* The whole feed, in an overlay rather than an expanded card. The list
          scrolls inside the dialog, so however long the feed grows the
          dashboard behind it keeps its layout. */}
      <Modal
        open={activityOpen}
        onClose={() => setActivityOpen(false)}
        title="Recent Activity"
        description={`${recentActivity.length} campaign events and patient enrolments`}
        // Wider than the default: these rows carry a title, a masked number
        // and a right-aligned timestamp, which crowd each other at max-w-lg.
        className="max-w-xl"
      >
        <ActivityFeed items={recentActivity} />
      </Modal>
    </div>
  );
};

export default Dashboard;
